import { useParams, useSearchParams } from 'react-router-dom';
import PageHeader from 'src/components/page-header/page-header';
import css from './details.module.scss';
import { useGetBlock, useGetBlocks, useGetCards, useGetTags } from '../data/queries';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChipsFilter, ChipsType, ContactCard, ContactFilters, ContactFormProps, Tag } from 'src/types';
import SearchForm from '../filters/filter';
import Sceletons from '../sceletons';
import { CONTACTS } from 'src/constants/routes';
import ContactCardDetails from './card-details';
import ContactFilter from './contact-filter';
import BlockChips from '../filters/block-filter';
import EmptyPlaceholder from '../empty-placeholder';
import FilterChips from 'src/components/filter-chips/filter-chips';

export default function ContactBlockDetails() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFilter = useMemo(() => {
    return {
      search: searchParams?.get('search') ?? '',
      tags: [],
      cards: []
    };
  }, [searchParams]);

  const [filter, setFilter] = useState<ContactFilters>(defaultFilter);
  const { data: block = {} } = useGetBlock(slug);
  const { data: cards = [], isFetching } = useGetCards({ ...filter, blockId: block.id });
  const { data: blocks = [] } = useGetBlocks();
  const { data: tags = [] } = useGetTags({ blockId: block.id });

  const handleSearch = (f: ContactFormProps) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    setFilter({ ...filter, ...f });
    f?.search ? nextSearchParams.set('search', f?.search) : nextSearchParams.delete('search');
    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
  };

  const handleFilter = (data: Partial<ContactFilters>) => setFilter({ ...filter, ...data });
  const clearFilter = useCallback(() => setFilter({ ...defaultFilter }), [defaultFilter]);

  const selectedFilters = cards
    ?.filter((c: ContactCard) => filter.cards?.includes(c.id))
    .map((c: ContactCard) => {
      return { id: c.id, name: c.title, type: ChipsType.branch };
    })
    .concat(
      tags
        .filter((t: Tag) => t.id && filter.tags?.includes(t.id))
        ?.map((b: Tag) => {
          return { id: b.id, name: b.name, type: ChipsType.tag };
        })
    );

  const deselectFilter = (f: ChipsFilter) =>
    setFilter({
      ...filter,
      tags: filter.tags?.filter((t: string) => t !== f.id),
      cards: filter.cards?.filter((c: string) => c !== f.id)
    });

  const removeHTML = (input: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = input;
    return (tmp.textContent ?? tmp.innerText) || '';
  };

  useEffect(() => {
    clearFilter();
  }, [clearFilter, slug]);

  return (
    <main className={css.contacts}>
      <PageHeader
        title={block?.title ?? ''}
        pages={[{ path: `/${CONTACTS.relativePath}`, title: CONTACTS.title }]}
        description={removeHTML(block?.description)}
        filter={
          <>
            <SearchForm initialValues={{ search: filter.search }} onSubmit={handleSearch} />
            <BlockChips blocks={blocks} selectedBlock={slug} />
          </>
        }
      />
      <div className={css.body}>
        <div className={css.content}>
          <div className={css.list}>
            <div className={css.filter}>
              {selectedFilters?.length > 0 && (
                <FilterChips selectedFilters={selectedFilters} onClick={deselectFilter} onClear={clearFilter} />
              )}
              <ContactFilter
                cards={block?.cards?.map((c: ContactCard) => {
                  return { id: c.id, name: c.title };
                })}
                tags={tags}
                onSubmit={handleFilter}
                initialValues={filter}
              />
            </div>
            <div className={css.blockCards}>
              {isFetching && <Sceletons />}
              {cards?.map((c: ContactCard) => <ContactCardDetails key={c.id} card={c} />)}
              {!isFetching && cards?.length === 0 && <EmptyPlaceholder />}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
